import {otsBaseApi} from "../../ots-api";
import {SiteSiteAssoc} from "../../../model/site-site-assoc";
import {removeUndefined} from "../../../util/util";
import {ImportType} from "../../../model/import-type";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["SiteSiteAssocs", "SourceSiteSiteAssocs"]});

const siteSiteAssocApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllSiteSiteAssocs: builder.query<SiteSiteAssoc[], string | undefined>({
      query: (regionId) => ({
        url: `/site-site-assocs/all`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId
        })
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "SiteSiteAssocs", id} as const)),
            ...result.map(({sourceSiteId}) => ({type: "SourceSiteSiteAssocs", id: sourceSiteId} as const)),
            {type: "SiteSiteAssocs", id: "LIST"}
          ];
        } else {
          return [{type: "SiteSiteAssocs", id: "LIST"}];
        }
      }
    }),
    getSiteSiteAssocBySourceSite: builder.query<
      SiteSiteAssoc[],
      { siteId: string, regionId?: string }
    >({
      query: ({siteId, regionId}) => ({
        url: `/site-site-assocs/all/source/${siteId}`,
        method: 'GET',
        params: removeUndefined({
          regionId: regionId
        }),
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "SiteSiteAssocs", id} as const)),
            ...result.map(({sourceSiteId}) => ({type: "SourceSiteSiteAssocs", id: sourceSiteId} as const)),
            {type: "SiteSiteAssocs", id: "LIST"}
          ];
        } else {
          return [{type: "SiteSiteAssocs", id: "LIST"}];
        }
      },
    }),
    getSiteSiteAssoc: builder.query<SiteSiteAssoc, string>({
      query: (id) => ({
        url: `/site-site-assocs/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "SiteSiteAssocs", id}]
    }),
    getSiteSiteAssocImportTypes: builder.query<ImportType[], void>({
      query: () => ({
        url: `/site-site-assocs/import-types`,
        method: "GET",
      }),
    }),
    createSiteSiteAssoc: builder.mutation<SiteSiteAssoc, Partial<SiteSiteAssoc>>({
      query: (mapping) => ({
        url: "/site-site-assocs",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "SiteSiteAssocs", id: "LIST"},
        {type: "SourceSiteSiteAssocs"},
      ]
    }),
    updateSiteSiteAssoc: builder.mutation<SiteSiteAssoc, Partial<SiteSiteAssoc>>({
      query: (mapping) => ({
        url: "/site-site-assocs",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: [
        {type: "SiteSiteAssocs", id: "LIST"},
        {type: "SourceSiteSiteAssocs"},
      ]
    }),
    deleteSiteSiteAssoc: builder.mutation<void, Partial<SiteSiteAssoc>>({
      query: (mapping) => ({
        url: "/site-site-assocs",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: [
        {type: "SiteSiteAssocs", id: "LIST"},
        {type: "SourceSiteSiteAssocs"},
      ]
    }),
    importSiteSiteAssocs: builder.mutation<void, { importType: ImportType, file: File }>({
      query: ({importType, file}) => {
        const formData = new FormData()
        formData.append("type", importType.code)
        formData.append("file", file)
        return ({
          url: `/site-site-assocs/import`,
          method: 'POST',
          body: formData,
        })
      },
      invalidatesTags: (_result, _error, _arg, _meta) => {
        return [
          {type: 'SiteSiteAssocs'},
          {type: 'SiteSiteAssocs', id: 'LIST'},
          {type: "SourceSiteSiteAssocs"},
        ]
      },
    })
  })
});

export const {
  useGetAllSiteSiteAssocsQuery,
  useGetSiteSiteAssocBySourceSiteQuery,
  useGetSiteSiteAssocQuery,
  useGetSiteSiteAssocImportTypesQuery,
  useCreateSiteSiteAssocMutation,
  useUpdateSiteSiteAssocMutation,
  useDeleteSiteSiteAssocMutation,
  useImportSiteSiteAssocsMutation,
} = siteSiteAssocApi;
